import { Button, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { PANEL } from '@schooly/constants/test-ids';
import { useFlag } from '@schooly/hooks/use-flag';
import { CheckIcon, CrossIcon } from '@schooly/style';
import {
  forwardRef,
  PropsWithChildren,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

type FiltersSidebarProps = PropsWithChildren<{
  open?: boolean;
  title: string;
  onOpen?: () => void;
  footer?: React.ReactNode;
  onClear?: () => void;
  onApply: () => void;
  canBeClosed?: boolean;
}>;

export type FiltersSidebarMethods = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  preventClose: () => void;
  allowClose: () => void;
};

export const FiltersSidebar = forwardRef<FiltersSidebarMethods, FiltersSidebarProps>(
  (
    {
      open: openProp,
      children,
      title,
      footer,
      onClear,
      onApply,
      onOpen,
      canBeClosed: canBeClosedProp = true,
    },
    ref,
  ) => {
    const [isOpen, open, close] = useFlag(!!openProp);
    const canBeClosed = useRef(canBeClosedProp);
    const { formatMessage } = useIntl();
    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          onOpen?.();
          open();
        },
        close,
        isOpen,
        preventClose: () => {
          canBeClosed.current = false;
        },
        allowClose: () => {
          canBeClosed.current = true;
        },
      }),
      [open, close, isOpen, onOpen],
    );

    useEffect(() => {
      if (openProp !== undefined) return;
      openProp ? open() : close();
    }, [openProp, open, close]);

    const onClickAway = useCallback(() => {
      if (canBeClosed.current !== true) return;
      onApply();
      close();
    }, [close, onApply]);

    return (
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClickAway}
        PaperProps={{ 'data-test-id': PANEL.CONTAINER }}
      >
        <Stack width={400} flexDirection="column" gap={2} px={2.5} pt={4} pb={9.5}>
          <Stack flexDirection="row" gap={2}>
            <Stack flex={1}>
              <Typography variant="h2">{title}</Typography>
            </Stack>
            <IconButton inverse onClick={close}>
              <CrossIcon />
            </IconButton>
          </Stack>
          {children}
        </Stack>
        <Stack
          flexDirection="row"
          position="fixed"
          width={400}
          bgcolor="background.paper"
          bottom={0}
          px={2.5}
          pb={2}
          right={0}
          gap={2}
        >
          <Button
            variant="contained"
            onClick={() => {
              close();
              onApply();
            }}
            startIcon={<CheckIcon />}
          >
            {formatMessage({ id: 'filter-Apply-filter' })}
          </Button>
          {onClear && (
            <Button variant="outlined" onClick={onClear}>
              {formatMessage({ id: 'filter-Clear-short' })}
            </Button>
          )}
          {footer}
        </Stack>
      </Drawer>
    );
  },
);

export const useFiltersSidebar = (ref: Ref<FiltersSidebarMethods>) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  useImperativeHandle(ref, () => sidebar.current as FiltersSidebarMethods);

  const { allowClose, preventClose } = useMemo(
    () => ({
      allowClose: (shouldTrigger = true) => {
        if (!shouldTrigger) return;
        sidebar.current?.allowClose();
      },
      preventClose: () => {
        sidebar.current?.preventClose();
      },
    }),
    [],
  );

  return { sidebar, allowClose, preventClose };
};
